// src/pages/Inventory.js
import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
    FiServer, FiHome, FiTruck, FiPlus, FiEdit, FiSave, 
    FiTrash2, FiPackage, FiX 
} from 'react-icons/fi';

import { AuthContext } from '../context/AuthContext';
import InventoryListView from './InventoryListView';

// --- Add Site Modal ---
const AddSiteModal = ({ isOpen, onClose, onAddSite, technicians = [] }) => {
    const [newSite, setNewSite] = useState({ 
        name: '', 
        type: 'Office', 
        city: '', 
        licensePlate: '', 
        assignedTechnician: '' 
    });
    const overlayMousedownTarget = useRef(null);
  
    useEffect(() => {
        if (isOpen) {
            setNewSite({ name: '', type: 'Office', city: '', licensePlate: '', assignedTechnician: '' });
        }
    }, [isOpen]);

    if (!isOpen) return null;
  
    const handleMouseDown = (e) => { overlayMousedownTarget.current = e.target; };
    const handleMouseUp = (e) => {
      if (overlayMousedownTarget.current === e.target && e.target === e.currentTarget) {
        onClose();
      }
      overlayMousedownTarget.current = null;
    };
  
    const handleChange = (e) => {
      const { name, value } = e.target;
      setNewSite(prev => ({ ...prev, [name]: value }));
    };
  
    const handleSubmit = (e) => {
      e.preventDefault();
      if (!newSite.name) {
          alert('Please provide a name for the location.');
          return;
      }
      const payload = {
        name: newSite.name,
        type: newSite.type,
      };
      if (newSite.type === 'Office') {
          payload.address = { city: newSite.city };
      } else {
          payload.licensePlate = newSite.licensePlate;
          if (newSite.assignedTechnician) {
              payload.assignedTechnician = newSite.assignedTechnician;
          }
      }
      onAddSite(payload);
      onClose();
    };

    return (
      <div className="modal-overlay" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
        <div className="modal-content">
          <div className="modal-header">
            <h3>Add New Location</h3>
            <button onClick={onClose} className="modal-close-btn"><FiX /></button>
          </div>
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
                <label>Location Type</label>
                <select name="type" value={newSite.type} onChange={handleChange} className="form-select" autoFocus>
                    <option value="Office">Office</option>
                    <option value="Truck">Truck</option>
                </select>
            </div>
            <div className="form-group">
                <label>Location Name</label>
                <input 
                    type="text" 
                    name="name" 
                    value={newSite.name} 
                    onChange={handleChange} 
                    className="form-input"
                    required 
                    placeholder={newSite.type === 'Truck' ? "e.g. Service Van 4" : "e.g. Main Warehouse"}
                />
            </div>
            {newSite.type === 'Office' ? (
                <div className="form-group">
                    <label>City (Optional)</label>
                    <input type="text" name="city" value={newSite.city} onChange={handleChange} className="form-input" placeholder="e.g. New York"/>
                </div>
            ) : (
                <>
                    <div className="form-group">
                        <label>License Plate (Optional)</label>
                        <input type="text" name="licensePlate" value={newSite.licensePlate} onChange={handleChange} className="form-input" placeholder="e.g. ABC-1234" />
                    </div>
                    <div className="form-group">
                        <label>Assign Technician (Optional)</label>
                        <select name="assignedTechnician" value={newSite.assignedTechnician} onChange={handleChange} className="form-select">
                            <option value="">-- Unassigned --</option>
                            {technicians.map(tech => (
                                <option key={tech._id} value={tech._id}>{tech.name}</option>
                            ))}
                        </select>
                    </div>
                </>
            )}
            <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Location</button>
            </div>
          </form>
        </div>
      </div>
    );
};

const SiteCard = ({ site, onSelectSite, isEditing, isSelected }) => {
    const isOffice = site.type === 'Office';
    const cardClasses = `community-card ${isEditing ? 'editing' : ''} ${isSelected ? 'selected' : ''}`;
    const handleClick = () => {
        isEditing ? onSelectSite(site._id, false) : onSelectSite(site._id, true);
    };

    return (
        <div className={cardClasses} onClick={handleClick}>
            {isEditing && (
                <div className="card-checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        className="custom-checkbox"
                        checked={isSelected}
                        onChange={() => onSelectSite(site._id, false)}
                    />
                </div>
            )}
            <div className="community-card-icon">{isOffice ? <FiHome size={32} /> : <FiTruck size={32} />}</div>
            <h3 className="community-card-name">{site.name}</h3>
            <p className="community-card-city">{site.address?.city || site.type}</p>
        </div>
    );
};

const OwnerGridView = ({ owners, onSelectOwner }) => {
  return (
    <div className="community-grid-container">
      {owners.map(owner => (
        <div key={owner._id} className="community-card" onClick={() => onSelectOwner(owner)}>
          <div className="community-card-icon"><FiHome size={32} /></div>
          <h3 className="community-card-name">{owner.name}</h3>
          <p className="community-card-city">{owner.address?.city}</p>
        </div>
      ))}
    </div>
  );
};

// --- MAIN PAGE COMPONENT ---
const Inventory = ({ 
    sites, communities, inventory, technicians, onAddItem, onAdjustStock, 
    onAddSite, onDeleteMultipleSites, onDeleteMultipleInventoryItems, 
    masterInventoryItems, onTransferInventory, onConsumeItem 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerType, setOwnerType] = useState(null); 
  const [inventoryMode, setInventoryMode] = useState('sites');
  const [isAddSiteModalOpen, setAddSiteModalOpen] = useState(false);
  const [isEditingSites, setIsEditingSites] = useState(false);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);

  // --- PERMISSION CHECKS ---
  const canManageMasterItems = useMemo(() => user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), [user]);
  
  // ✅ Restricted permission check for location management
  const canManageLocations = useMemo(() => 
    user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), 
    [user]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const siteIdParam = searchParams.get('siteId');
    const communityIdParam = searchParams.get('communityId');
    
    if (!siteIdParam && !communityIdParam) {
        setSelectedOwner(null);
        setOwnerType(null);
        return;
    }

    if (siteIdParam && sites?.length) {
        const site = sites.find(s => s._id === siteIdParam);
        if (site) {
            setInventoryMode('sites');
            setSelectedOwner(site);
            setOwnerType('Site');
            return;
        }
    } 
    
    if (communityIdParam && communities?.length) {
        const comm = communities.find(c => c._id === communityIdParam);
        if (comm) {
            setInventoryMode('communities');
            setSelectedOwner(comm);
            setOwnerType('Community');
            return;
        }
    }
  }, [location.search, sites, communities]); 

  useEffect(() => {
      if (!isEditingSites) {
          setSelectedSiteIds([]);
      }
  }, [isEditingSites]);

  const handleSelectSite = (siteId, forceNavigate = false) => {
    // ✅ Check permission before allowing item selection
    if (isEditingSites && canManageLocations && !forceNavigate) {
        setSelectedSiteIds(prev =>
            prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]
        );
    } else {
        const site = sites.find(s => s._id === siteId);
        if (site) {
            navigate(`/inventory?siteId=${site._id}`);
        }
    }
  };

  const handleBack = () => { navigate('/inventory'); };
  
  const handleModeToggle = (mode) => {
    if (inventoryMode !== mode) {
        setInventoryMode(mode);
        navigate('/inventory');
        setIsEditingSites(false);
        setSelectedSiteIds([]);
    }
  }

  // ✅ Blocking edit mode for regular users
  const handleEnterEditMode = () => {
    if (canManageLocations) setIsEditingSites(true);
  };
  
  const handleExitEditMode = () => {
      setIsEditingSites(false);
      setSelectedSiteIds([]);
  };
  
  const handleSelectAllSites = (e) => {
    if (e.target.checked) {
          setSelectedSiteIds(sites.map(s => s._id));
      } else {
          setSelectedSiteIds([]);
      }
  };
  
  const handleDeleteSelectedSites = () => {
      if (window.confirm(`Are you sure you want to delete ${selectedSiteIds.length} selected locations?`)) {
          onDeleteMultipleSites(selectedSiteIds);
          setSelectedSiteIds([]);
      }
  };
  
  const offices = sites.filter(s => s.type === 'Office');
  const trucks = sites.filter(s => s.type === 'Truck');
  const isAllSitesSelected = sites.length > 0 && selectedSiteIds.length === sites.length;

  if (selectedOwner && ownerType) {
    return (
      <div className="page-container">
        <InventoryListView 
          owner={selectedOwner}
          ownerType={ownerType}
          allItems={inventory}
          onBack={handleBack}
          onAddItem={onAddItem}
          onAdjustStock={onAdjustStock}
          onDeleteMultipleInventoryItems={onDeleteMultipleInventoryItems}
          masterInventoryItems={masterInventoryItems}
          onTransferStock={onTransferInventory}
          sites={sites}
          communities={communities} 
          onConsumeItem={onConsumeItem} 
        />
      </div>
    );
  }

  return (
    <div className="page-container">
       <AddSiteModal isOpen={isAddSiteModalOpen} onClose={() => setAddSiteModalOpen(false)} onAddSite={onAddSite} technicians={technicians}/>
       <div className="page-header">
          <div>
              <h2 className="page-title">Inventory Management</h2>
              <span className="page-subtitle">Select a location to view its inventory</span>
          </div>
        
          {/* ✅ UPDATED: Added flexWrap to allow buttons to wrap on mobile */}
          <div className="page-actions" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {canManageMasterItems && (
                <button className="btn btn-primary" onClick={() => navigate('/master-inventory')}>
                  <FiPackage className="btn-icon"/> Manage Master Items
                </button>
            )}
            
            {/* ✅ Only render edit controls for Admin/Giga Admin */}
            {canManageLocations && (
                <div className={`edit-locations-container ${inventoryMode !== 'sites' ? 'hidden' : ''}`}>
                    {!isEditingSites ? (
                        <button className="btn btn-secondary" onClick={handleEnterEditMode}>
                            <FiEdit size={16} className="btn-icon" /> Edit Locations
                        </button>
                    ) : (
                        <>
                            <button className="btn btn-primary" onClick={() => setAddSiteModalOpen(true)}>
                                <FiPlus size={16} className="btn-icon" /> Add Location
                            </button>
                            <button className="btn btn-secondary" onClick={handleExitEditMode}>
                                <FiSave size={16} className="btn-icon" /> Save
                            </button>
                        </>
                    )}
                </div>
            )}
    
            <div className="segmented-control">
                <button className={`segment-btn ${inventoryMode === 'sites' ? 'active' : ''}`} onClick={() => handleModeToggle('sites')}><FiServer/> Offices & Trucks</button>
                <button className={`segment-btn ${inventoryMode === 'communities' ? 'active' : ''}`} onClick={() => handleModeToggle('communities')}><FiHome/> Communities</button>
            </div>
          </div>
      </div>
      
      {/* ✅ Bulk selection bar visibility restricted by role */}
      {inventoryMode === 'sites' && isEditingSites && canManageLocations && (
          <div className="table-card" style={{ marginBottom: '1.5rem', overflow: 'visible' }}>
              {selectedSiteIds.length > 0 ? (
                <div className="table-controls bulk-actions-bar">
                    <div className="bulk-actions-info"><span>{selectedSiteIds.length}</span> location{selectedSiteIds.length > 1 ? 's' : ''} selected</div>
                    <div className="bulk-actions-buttons">
                        <button className="btn btn-danger" onClick={handleDeleteSelectedSites}><FiTrash2 className="btn-icon" /> Delete Selected</button>
                    </div>
                </div>
            ) : (
                <div className="table-controls">
                    <div className="select-all-wrapper">
                        <input type="checkbox" id="select-all-sites" className="custom-checkbox" checked={isAllSitesSelected} onChange={handleSelectAllSites} />
                        <label htmlFor="select-all-sites">Select All Locations</label>
                    </div>
                </div>
            )}
          </div>
      )}
      
      {inventoryMode === 'sites' ? (
        <>
            <div>
                <h3 className="location-section-title">Offices</h3>
                <div className="community-grid-container">
                    {offices.map(site => (
                        <SiteCard 
                            key={site._id}
                            site={site}
                            onSelectSite={handleSelectSite}
                            // ✅ Disable checkboxes for regular users
                            isEditing={isEditingSites && canManageLocations}
                            isSelected={selectedSiteIds.includes(site._id)}
                        />
                    ))}
                </div>
            </div>
            <hr className="section-divider" />
            <div>
                <h3 className="location-section-title">Trucks</h3>
                <div className="community-grid-container">
                    {trucks.map(site => (
                        <SiteCard 
                            key={site._id}
                            site={site}
                            onSelectSite={handleSelectSite}
                            // ✅ Disable checkboxes for regular users
                            isEditing={isEditingSites && canManageLocations}
                            isSelected={selectedSiteIds.includes(site._id)}
                        />
                    ))}
                </div>
            </div>
        </>
      ) : (
        <OwnerGridView owners={communities} onSelectOwner={(owner) => navigate(`/inventory?communityId=${owner._id}`)} />
      )}
  </div>
  );
};

export default Inventory;